import type { FluxErrorEntityErrorCodes } from '../types.js';

export const InterpolationErrorCodes = {
  InterpolationInvalidSyntax: {
    code: 'interpolation/invalid-syntax',
    explanation: `Houve um erro ao tentar interpolar seu texto, isso pode ser devido a uma sintaxe inválida ou a um erro interno do sistema.`,
    localizations: {},
    message: 'Houve um erro ao tentar interpolar a seu texto.',
    supportingDocs: [
      {
        name: 'Conceitos gerais',
        path: '/wiki/docs/interpolation/conceitos',
      },
      {
        name: 'Sintaxe e Linguagem',
        path: '/wiki/docs/category/sintaxe-e-linguagem',
      },
      {
        name: 'Utilitários',
        path: '/wiki/docs/category/referência-de-utilitários',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
    ],
  },
} satisfies FluxErrorEntityErrorCodes<'interpolation'>;

export const InterpolationHelperErrorCodes = {
  InterpolationHelperArgumentsInvalidEnum: {
    code: 'interpolation-helper/arguments-invalid-enum',
    explanation: `Este erro ocorre quando um utilitário de interpolação recebe um argumento com um valor inválido para sua execução. O argumento inválido é identificado no sufixo do código de erro, seguindo o padrão \`{#ERROR:interpolation-helper/arguments-invalid-enum-><argumento>}\`.

**Causas comuns:**
- Um utilitário recebeu um argumento com um valor que não pertence a lista de valores permitidos.

**Como resolver:**
Verifique se os argumentos estão corretos e se eles estão no formato esperado pelo utilitário.
`,
    localizations: {},
    message: 'Argumentos inválidos para o utilitário de interpolação.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Utilitários',
        path: '/wiki/docs/category/referência-de-utilitários',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
    ],
  },
  InterpolationHelperArgumentsInvalidType: {
    code: 'interpolation-helper/arguments-invalid-type',
    explanation: `Este erro ocorre quando um utilitário de interpolação recebe um argumento com um tipo inválido para sua execução. O argumento inválido é identificado no sufixo do código de erro, seguindo o padrão \`{#ERROR:interpolation-helper/arguments-invalid-type-><argumento>}\`.

**Causas comuns:**
- Um utilitário recebeu um argumento com um tipo inválido
- Não foi possível converter o argumento para o tipo esperado

**Como resolver:**
Verifique se os argumentos estão corretos e se eles estão no formato esperado pelo utilitário.
`,
    localizations: {},
    message: 'Argumentos inválidos para o utilitário de interpolação.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Utilitários',
        path: '/wiki/docs/category/referência-de-utilitários',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
    ],
  },
  InterpolationHelperArgumentsMissing: {
    code: 'interpolation-helper/arguments-missing',
    explanation: `Este erro ocorre quando um utilitário de interpolação não recebe todos os argumentos obrigatórios para sua execução. O argumento ausente é identificado no sufixo do código de erro, seguindo o padrão \`{#ERROR:interpolation-helper/arguments-missing-><argumento>}\`.

**Causas comuns:**
- Argumentos obrigatórios não informados
- Sintaxe de chamada do utilitário incorreta

**Como resolver:**
Certifique-se de que todos os parâmetros exigidos pelo utilitário foram fornecidos corretamente na expressão de interpolação.
`,
    localizations: {},
    message: 'Argumentos obrigatórios ausentes para o utilitário de interpolação.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Utilitários',
        path: '/wiki/docs/category/referência-de-utilitários',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
    ],
  },
  InterpolationHelperCsvFormatError: {
    code: 'interpolation-helper/csv-format-error',
    explanation: `Houve um erro ao tentar formatar os dados para CSV. Isso pode acontecer se os dados estiverem em um formato incompatível ou se as opções fornecidas forem inválidas.

**Causas comuns:**
- Dados fornecidos não são uma lista (array)
- Opções de formatação inválidas
- Erro interno na biblioteca de CSV

**Como resolver:**
Certifique-se de que o primeiro argumento é uma lista de objetos ou listas e que as opções de formatação estão corretas.
`,
    localizations: {},
    message: 'Erro ao formatar para CSV.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `formatCsv`',
        path: '/wiki/docs/interpolation/utilitarios/format#csvFormat',
      },
    ],
  },
  InterpolationHelperCsvParseError: {
    code: 'interpolation-helper/csv-parse-error',
    explanation: `Houve um erro ao tentar converter a string CSV para objeto. A string fornecida pode estar mal formatada ou as opções de parsing podem estar incorretas.

**Causas comuns:**
- String CSV mal formatada
- Delimitador incorreto
- Erro no processamento dos cabeçalhos

**Como resolver:**
Verifique se a string CSV está formatada corretamente e se as opções de parsing (como delimitador e cabeçalhos) correspondem ao conteúdo.
`,
    localizations: {},
    message: 'Erro ao converter string CSV para objeto.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `parseCsv`',
        path: '/wiki/docs/interpolation/utilitarios/format#csvParse',
      },
    ],
  },
  InterpolationHelperFormatDateInvalidDate: {
    code: 'interpolation-helper/format-date-invalid-date',
    explanation: `A data fornecida para formatação é inválida, isso pode ser devido a um erro de intervalo.

**Causas comuns:**
- Data fora do intervalo permitido
- Data mal formatada
- Números negativos

**Como resolver:**
Verifique se a data está no formato correto e dentro do intervalo permitido.
`,
    localizations: {},
    message: 'A data fornecida para formatação é inválida.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `formatDate`',
        path: '/wiki/docs/interpolation/utilitarios/date#formatDate',
      },
    ],
  },
  InterpolationHelperFormatDateInvalidFormat: {
    code: 'interpolation-helper/format-date-invalid-format',
    explanation: `O formato de data fornecido para formatação é inválido, isso pode ser devido a um erro de sintaxe.

**Causas comuns:**
- Formato de data inválido
- Caracteres inválidos

**Como resolver:**
Verifique se o formato de data está no formato correto.
`,
    localizations: {},
    message: 'O formato de data fornecido para formatação é inválido.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `formatDate`',
        path: '/wiki/docs/interpolation/utilitarios/date#formatDate',
      },
    ],
  },
  InterpolationHelperFromJsonInvalid: {
    code: 'interpolation-helper/from-json-invalid',
    explanation: `Houve um erro ao tentar converter a string JSON para objeto. A string fornecida não é um um JSON válido.

**Causas comuns:**
- String JSON mal formatada
- Aspas faltando ou incorretas
- Vírgulas extras

**Como resolver:**
Verifique se a string JSON está formatada corretamente utilizando um validador de JSON.
`,
    localizations: {},
    message: 'Erro ao converter string JSON para objeto.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `fromJson`',
        path: '/wiki/docs/interpolation/utilitarios/format#fromJson',
      },
    ],
  },
  InterpolationHelperGetError: {
    code: 'interpolation-helper/get-error',
    explanation: `Houve um erro ao tentar acessar uma propriedade do objeto.

**Causas comuns:**
- Objeto inválido ou corrompido durante a execução

**Como resolver:**
Verifique o objeto e o caminho que está tentando acessar.
`,
    localizations: {},
    message: 'Erro ao acessar propriedade do objeto.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `get`',
        path: '/wiki/docs/interpolation/utilitarios/core#get',
      },
    ],
  },
  InterpolationHelperGetRecursionLimit: {
    code: 'interpolation-helper/get-recursion-limit',
    explanation: `O limite de profundidade de recursão ao acessar propriedades do objeto foi excedido.

**Causas comuns:**
- Caminho muito profundo ou referências circulares complexas

**Como resolver:**
Simplifique a estrutura do objeto ou reduza a profundidade do caminho.
`,
    localizations: {},
    message: 'Limite de recursão excedido ao acessar propriedade.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `get`',
        path: '/wiki/docs/interpolation/utilitarios/core#get',
      },
    ],
  },
  InterpolationHelperGetRestrictedKey: {
    code: 'interpolation-helper/get-restricted-key',
    explanation: `Tentativa de acesso a uma chave restrita do objeto.

**Causas comuns:**
- Tentativa de acesso a propriedades internas como __proto__, constructor ou prototype.

**Como resolver:**
Não acesse propriedades internas protegidas do javascript.
`,
    localizations: {},
    message: 'Acesso a chave restrita não permitido.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `get`',
        path: '/wiki/docs/interpolation/utilitarios/core#get',
      },
    ],
  },
  InterpolationHelperMathDivisionByZero: {
    code: 'interpolation-helper/math-division-by-zero',
    explanation: `Tentativa de divisão por zero detectada.

**Causas comuns:**
- O divisor (segundo argumento) é 0

**Como resolver:**
Verifique os valores passados para a função de divisão e garanta que o divisor não seja zero.
`,
    localizations: {},
    message: 'Divisão por zero não é permitida.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `math`',
        path: '/wiki/docs/interpolation/utilitarios/math#math',
      },
      {
        name: 'Utilitário de `divide`',
        path: '/wiki/docs/interpolation/utilitarios/math#divide',
      },
    ],
  },
  InterpolationHelperMathInvalidOperation: {
    code: 'interpolation-helper/math-invalid-operation',
    explanation: `A operação matemática solicitada não é suportada ou é desconhecida.

**Causas comuns:**
- Nome da operação escrito incorretamente

**Como resolver:**
Verifique o nome da operação. As operações suportadas são: add, subtract, multiply, divide, ceil, floor, round.
`,
    localizations: {},
    message: 'Operação matemática desconhecida.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `math`',
        path: '/wiki/docs/interpolation/utilitarios/math#operações-suportadas',
      },
    ],
  },
  InterpolationHelperReplaceInvalidRegex: {
    code: 'interpolation-helper/replace-invalid-regex',
    explanation: `O padrão de expressão regular (Regex) fornecido é inválido. A expressão precisa seguir o formato de regex do Javascript.

**Causas comuns:**
- Erros de sintaxe na expressão regular
- Flags inválidas

**Como resolver:**
Verifique a sintaxe da sua expressão regular. Teste-a em ferramentas como regex101.com.
`,
    localizations: {},
    message: 'Padrão de expressão regular inválido.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `replace`',
        path: '/wiki/docs/interpolation/utilitarios/string#replace',
      },
    ],
  },
  InterpolationHelperReplaceUnsafeRegex: {
    code: 'interpolation-helper/replace-unsafe-regex',
    explanation: `O padrão de expressão regular (Regex) fornecido foi identificado como inseguro. Por motivos de segurança e performance, o utilitário de interpolação bloqueia expressões que podem causar negação de serviço (ReDoS) devido à complexidade excessiva ou backtracking catastrófico.

**Causas comuns:**
- Uso de quantificadores aninhados ou sobrepostos (ex: \`(a+)*\`)
- Expressões que podem levar a um tempo de execução exponencial

**Como resolver:**
Refatore a expressão regular para torná-la mais específica e eficiente. Utilize ferramentas de análise de Regex para identificar e corrigir possíveis vulnerabilidades de performance.
`,
    localizations: {},
    message: 'Padrão de expressão regular inválido.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `replace`',
        path: '/wiki/docs/interpolation/utilitarios/string#replace',
      },
    ],
  },
  InterpolationHelperToJsonCircular: {
    code: 'interpolation-helper/to-json-circular',
    explanation: `Houve um erro ao tentar converter o valor para JSON. Isso geralmente acontece quando o objeto contém referências circulares.

**Causas comuns:**
- Referências circulares no objeto

**Como resolver:**
Verifique se o objeto não contém referências circulares ou utilize um método customizado para serialização.
`,
    localizations: {},
    message: 'Erro ao converter valor para JSON: referência circular detectada.',
    supportingDocs: [
      {
        name: 'Chamando utilitários',
        path: '/wiki/docs/interpolation/sintaxe/utilitarios-basicos',
      },
      {
        name: 'Erros comuns',
        path: '/wiki/docs/interpolation/guias/erros-comuns',
      },
      {
        name: 'Utilitário de `toJson`',
        path: '/wiki/docs/interpolation/utilitarios/format#toJson',
      },
    ],
  },
} satisfies FluxErrorEntityErrorCodes<'interpolation-helper'>;
